import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { mockData } from '../utils/mockData';
import { Menu, X, ChevronDown } from 'lucide-react';

// Product slugs mapping
const productSlugs = {
  'Labels': 'branding-labels',
  'Etiketler': 'branding-labels',
  'Hang Tags': 'hang-tags',
  'Karton Etiketler': 'hang-tags',
  'Patches': 'care-composition',
  'Yamalar': 'care-composition',
  'Packaging': 'packaging',
  'Ambalaj': 'packaging',
  'Woven Labels': 'woven-labels',
  'Dokuma Etiketler': 'woven-labels'
};

const Navbar = ({ forceSolid = false }) => {
  const { language, setLanguage } = useLanguage();
  const content = mockData[language];
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const isSolid = scrolled || forceSolid;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownOpen && !event.target.closest('.language-dropdown')) {
        setLanguageDropdownOpen(false);
      }
      if (productsDropdownOpen && !event.target.closest('.products-dropdown')) {
        setProductsDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [languageDropdownOpen, productsDropdownOpen]);

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
    setMobileProductsOpen(false);
  };

  const handleLanguageSelect = (lang) => {
    setLanguage(lang);
    setLanguageDropdownOpen(false);
  };

  return (
    <>
      <nav data-testid="site-navigation" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid ? 'bg-white shadow-md py-4' : 'bg-transparent py-6'
      }`}>
        <div className="max-w-7xl mx-auto px-6">
          {/* Desktop Layout */}
          <div className="hidden xl:flex items-center justify-between">
            <Link to="/" data-testid="desktop-nav-home" className="flex items-center space-x-2 mr-12">
              <div className="text-4xl font-bold tracking-tight cursor-pointer hover:opacity-80 transition-opacity">
                <span className={isSolid ? 'text-gray-900' : 'text-white'}>ETITEKS</span>
              </div>
            </Link>

          {/* Desktop Menu */}
          <div className="flex items-center space-x-7">
            {/* Products Dropdown - Hover */}
            <div 
              className="relative products-dropdown"
              onMouseEnter={() => setProductsDropdownOpen(true)}
              onMouseLeave={() => setProductsDropdownOpen(false)}
            >
              <button
                data-testid="desktop-nav-products-toggle"
                aria-expanded={productsDropdownOpen}
                onClick={() => setProductsDropdownOpen(true)}
                className={`flex items-center space-x-1 text-base font-semibold transition-colors ${
                  isSolid ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-300'
                }`}
              >
                <span>{content.nav.products}</span>
                <ChevronDown size={20} className={`transition-transform ${productsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {productsDropdownOpen && (
                <div data-testid="desktop-nav-products-menu" className="absolute top-full left-0 pt-2 w-72 z-50">
                  <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 overflow-hidden border border-gray-100">
                    {content.products.items.map((item, index) => {
                      const slug = productSlugs[item.title];
                      return (
                        <Link
                          key={index}
                          to={`/products/${slug}`}
                          data-testid={`desktop-nav-product-${slug}`}
                          onClick={() => {
                            setProductsDropdownOpen(false);
                          }}
                          className="block px-6 py-4 text-lg font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        >
                          {item.title}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <Link to="/capabilities" data-testid="desktop-nav-capabilities" className={`text-base font-semibold transition-colors hover:text-blue-600 ${
              isSolid ? 'text-gray-700' : 'text-white'
            }`}>
              {content.nav.capabilities}
            </Link>
            <Link to="/sustainability" data-testid="desktop-nav-sustainability" className={`text-base font-semibold transition-colors hover:text-blue-600 ${
              isSolid ? 'text-gray-700' : 'text-white'
            }`}>
              {content.nav.sustainability}
            </Link>
            <Link to="/references" data-testid="desktop-nav-references" className={`text-base font-semibold transition-colors hover:text-blue-600 ${
              isSolid ? 'text-gray-700' : 'text-white'
            }`}>
              {content.nav.references}
            </Link>
            <Link to="/blog" data-testid="desktop-nav-blog" className={`text-base font-semibold transition-colors hover:text-blue-600 ${
              isSolid ? 'text-gray-700' : 'text-white'
            }`}>
              {content.nav.blog}
            </Link>
            <Link to="/about" data-testid="desktop-nav-about" className={`text-base font-semibold transition-colors hover:text-blue-600 ${
              isSolid ? 'text-gray-700' : 'text-white'
            }`}>
              {content.nav.about}
            </Link>
            <Link to="/faq" data-testid="desktop-nav-faq" className={`text-base font-semibold transition-colors hover:text-blue-600 ${
              isSolid ? 'text-gray-700' : 'text-white'
            }`}>
              FAQ
            </Link>
            <Link to="/contact" data-testid="desktop-nav-contact" className={`text-base font-semibold transition-colors hover:text-blue-600 ${
              isSolid ? 'text-gray-700' : 'text-white'
            }`}>
              {content.nav.contact}
            </Link>
          </div>

          {/* Right side buttons */}
          <div className="flex items-center space-x-4">
            {/* Get a Quote Button */}
            <Link
              to="/contact"
              data-testid="desktop-nav-get-quote"
              className="px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors shadow-md text-base whitespace-nowrap"
            >
              {content.hero?.ctaQuote || 'Get a Quote'}
            </Link>

            {/* Language removed - English only */}
          </div>
          </div>

          {/* Mobile Layout */}
          <div className="xl:hidden flex items-center justify-between">
            {/* Mobile - no language dropdown */}

            {/* Logo - Center */}
            <Link to="/" data-testid="mobile-nav-home" className="absolute left-1/2 transform -translate-x-1/2">
              <div className="text-xl font-bold tracking-tight cursor-pointer hover:opacity-80 transition-opacity">
                <span className={isSolid ? 'text-gray-900' : 'text-white'}>ETITEKS</span>
              </div>
            </Link>

            {/* Hamburger Menu - Right */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-nav-toggle"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation-panel"
              className={`p-2 rounded-lg transition-colors ${
                isSolid ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div data-testid="mobile-nav-overlay" className={`fixed inset-0 z-40 xl:hidden transition-all duration-300 ${
        mobileMenuOpen ? 'visible' : 'invisible'
      }`}>
        {/* Backdrop */}
        <div 
          data-testid="mobile-nav-backdrop"
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            mobileMenuOpen ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => setMobileMenuOpen(false)}
        ></div>

        {/* Menu Panel */}
        <div id="mobile-navigation-panel" data-testid="mobile-nav-panel" className={`absolute top-20 right-0 bottom-0 w-64 bg-white shadow-2xl transform transition-transform duration-300 ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="flex flex-col h-full">
            {/* Menu Items - No Header */}
            <div className="flex-1 overflow-y-auto py-6">
              <div className="flex flex-col space-y-1 px-4">
              <div className="flex flex-col">
                <button
                  onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                  data-testid="mobile-nav-products-toggle"
                  aria-expanded={mobileProductsOpen}
                  className="flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  <span>{content.nav.products}</span>
                  <ChevronDown size={18} className={`transition-transform ${mobileProductsOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileProductsOpen && (
                  <div data-testid="mobile-nav-products-menu" className="pl-4 pb-2">
                    {content.products.items.map((item, index) => {
                      const slug = productSlugs[item.title];
                      return (
                        <Link
                          key={index}
                          to={`/products/${slug}`}
                          onClick={handleLinkClick}
                          data-testid={`mobile-nav-product-${slug}`}
                          className="block px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-100 hover:text-blue-600 rounded-lg transition-colors"
                        >
                          {item.title}
                        </Link>
                      );
                    })}
                  </div>
                )}
                </div>
                <Link
                  to="/capabilities"
                  data-testid="mobile-nav-capabilities"
                  onClick={handleLinkClick}
                  className="px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  {content.nav.capabilities}
                </Link>
                <Link
                  to="/sustainability"
                  data-testid="mobile-nav-sustainability"
                  onClick={handleLinkClick}
                  className="px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  {content.nav.sustainability}
                </Link>
                <Link
                  to="/references"
                  data-testid="mobile-nav-references"
                  onClick={handleLinkClick}
                  className="px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  {content.nav.references}
                </Link>
                <Link
                  to="/blog"
                  data-testid="mobile-nav-blog"
                  onClick={handleLinkClick}
                  className="px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  {content.nav.blog}
                </Link>
                <Link
                  to="/about"
                  data-testid="mobile-nav-about"
                  onClick={handleLinkClick}
                  className="px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  {content.nav.about}
                </Link>
                <Link
                  to="/faq"
                  data-testid="mobile-nav-faq"
                  onClick={handleLinkClick}
                  className="px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  FAQ
                </Link>
                <Link
                  to="/contact"
                  data-testid="mobile-nav-contact"
                  onClick={handleLinkClick}
                  className="px-4 py-3 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  {content.nav.contact}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;